import { META_TYPES } from '../constants/visitMetadata';

/**
 * rxEntry — the ONE predicate for "this saved metadata entry is a digital
 * prescription with content".
 *
 * Hosts re-open the pad on the latest such entry (`editingRx` → `editMetaId` →
 * `replaces_meta_id` on the next save). Re-deriving the test inline let the
 * hosts drift from the pad: a DIAGNOSIS-only or SURGERIES-only Rx failed a
 * `medicines || investigations` check, so the pad re-opened EMPTY and the next
 * save created a DUPLICATE entry instead of replacing the one on screen.
 *
 * The rule mirrors the pad's own `hasRxContent`
 * (`DigitalPrescriptionForm.jsx`: `hasMeds || hasInv || hasSurg || hasDx`) —
 * anything the pad will save as a prescription entry is an entry the hosts must
 * be able to re-open. A chief-complaint-only or image-only row is NOT Rx
 * content there, and is not here either.
 *
 * The CONTENT half of that test is not written here. It was, identically, until
 * the 2026-09-26 catch-up merge found this module and
 * `utils/prescriptionContent.hasPrescriptionContent` had been written in
 * parallel with the same four terms — the same fork, twice over, that
 * `prescriptionContent`'s own header describes. So this is now the DIGITAL-TYPE
 * half plus a call into the one content predicate; the terms live in exactly
 * one place and cannot drift apart again.
 */
import { hasPrescriptionContent } from './prescriptionContent';

export function isDigitalRxEntry(entry) {
  if (!entry || entry.prescription_type !== 'digital') return false;
  return hasPrescriptionContent(entry);
}

/**
 * Group PRESCRIPTION_IMAGE rows onto their parent PRESCRIPTION entry as
 * `attached_images` (by prescription_meta_id, sorted by image_order). The pad
 * reads `initialData.attached_images`; the save response carries none, so every
 * host re-derives this from the metadata entries.
 */
export function groupRxImages(metadata) {
  const images = metadata.filter((e) => e.meta_type === META_TYPES.PRESCRIPTION_IMAGE);
  return metadata
    .filter((e) => e.meta_type === META_TYPES.PRESCRIPTION)
    .map((e) => ({
      ...e,
      attached_images: images
        .filter((img) => img.prescription_meta_id === e.meta_id)
        .sort((a, b) => (a.image_order ?? 0) - (b.image_order ?? 0)),
    }));
}

/** Latest non-superseded digital Rx with content (what hosts re-open in the pad). */
export function pickLatestDigitalRx(prescriptionEntries) {
  return prescriptionEntries
    .filter((e) => isDigitalRxEntry(e) && e.status !== 'superseded')
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] || null;
}
